/**
 * Auth Callback Page
 * Handles Google OAuth redirect and session creation
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Get session_id from URL fragment
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          console.error('No session_id found');
          navigate('/login');
          return;
        }

        // Exchange session_id for session_token.
        // NOTE: We intentionally do NOT use `credentials: 'include'` here so the
        // browser does not reject the response in cross-origin scenarios where
        // an upstream proxy may emit `Access-Control-Allow-Origin: *` together
        // with `Access-Control-Allow-Credentials: true` (a CORS spec violation).
        // We retrieve session_token from the response body and store it in
        // localStorage as a Safari-ITP-safe fallback.
        const response = await fetch(`${API_URL}/api/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId })
        });

        if (!response.ok) {
          const error = await response.json();
          console.error('Auth error:', error);
          alert(error.detail || 'Authentication failed');
          navigate('/login');
          return;
        }

        const userData = await response.json();

        // Safari ITP fallback: store token in localStorage so subsequent calls
        // can attach Authorization: Bearer <token> via axios interceptor.
        // This works even when cross-origin cookies are blocked by Safari.
        if (userData.session_token) {
          try { localStorage.setItem('ua_session_token', userData.session_token); }
          catch { /* localStorage may be unavailable in some private modes */ }
        }

        // Redirect to dashboard with user data
        navigate('/', { state: { user: userData } });

      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/login');
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-900 via-red-800 to-red-900 flex items-center justify-center">
      <div className="text-center">
        <RefreshCw className="w-12 h-12 text-white animate-spin mx-auto mb-4" />
        <p className="text-white text-lg">Authenticating...</p>
        <p className="text-red-200 text-sm mt-2">Sila tunggu sebentar</p>
      </div>
    </div>
  );
};

export default AuthCallback;
